import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { Merchant } from '../../../models/merchant/merchant';
import { FloatLabelType } from '@angular/material/form-field';
import { StreetAddressComponent } from '../../street-address/street-address.component';
import { MerchantService } from '../../../models/merchant/merchantService';
import {
  TextInputComponent,
  TextModes,
} from '../../ui/controls/text-input/text-input.component';
import { iValidatedControl } from '../../ui/controls/iValidatedControl';
import { StreetAddress } from '../../../models/streetAddress/streetAddress';
import { UIHelpers } from '../../../core/ui-helpers';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import {
  DdlInputComponent,
  DDLModes,
} from '../../ui/controls/ddl-input/ddl-input.component';
import {
  FormValidator,
  ValidationMessage,
} from '../../../models/validation/formValidator';
import { CacheService } from '../../../core/storage/cacheService';
import { BaseComponent } from '../../baseComponent';
import { FormMode } from '../../../core/constants';
import { StorageService } from '../../../core/storage/storageService';
import { AdminService } from '../../../models/admin/adminService';
import { ListItem } from '../../../../../../../libs/Shared/components/model/listItem';

@Component({
  selector: 'merchant-account-info',
  templateUrl: './merchant-account-info.component.html',
  standalone: false,
})
export class MerchantAccountInfoComponent extends BaseComponent {
  @Input() merchant: Merchant | undefined; //= Merchant.createEmpty();
  @Input() enableEditing: boolean = false;
  @Input() hideTitle: boolean = false;
  @Input() mode: FormMode = FormMode.View;
  @Output() saved = new EventEmitter<boolean>();
  @ViewChild('streetAddressForm') addressForm:
    | StreetAddressComponent
    | undefined;
  @ViewChildren(TextInputComponent) validatedTextBoxes:
    | QueryList<TextInputComponent>
    | undefined;
  @ViewChildren(DdlInputComponent) validatedDropDowns:
    | QueryList<DdlInputComponent>
    | undefined;

  data: Merchant = Merchant.createEmpty(); //= Merchant.createEmpty();

  inputTextModes = TextModes;
  ddlModes = DDLModes;

  timezoneOptions: ListItem[];

  newBrandName: string = '';
  brandOptions: ListItem[] = [];

  businessTypeOptions: ListItem[] = [];

  constructor(
    public merchantService: MerchantService,
    public uiHelpers: UIHelpers,
    private uiErrorHandler: UIErrorHandler,
    private formValidator: FormValidator,
    public storage: StorageService,
    private adminService: AdminService,
  ) {
    super();

    this.timezoneOptions = CacheService.timeZoneList;
  }

  ngOnInit() {
    this.businessTypeOptions = CacheService.businessTypesList;
    this.adminService.getMerchantBrands().then((response: string[]) => {
      let brandList = response.map((s) => new ListItem({ id: s, name: s }));
      brandList.push(new ListItem({ id: 'New Brand', name: 'New Brand' }));
      this.storage.setBrandList(brandList);
      this.brandOptions = this.storage.getBrandList();
    });
    if (this.merchant && this.data) {
      this.copyModelData(this.merchant, this.data);
    }
  }

  public businessTypeSelected(event: any) {
    this.data.businessType = event;
  }
  
  public brandSelected(event: any) {
    this.data.brandName = event;
  }

  public showBrandList() {
    this.data.brandName = '';
  }

  public getData(merchantData: Merchant) {
    if (this.data) {
      if (this.newBrandName != '') {
        this.data.brandName = this.newBrandName;
      }

      let address = this.addressForm?.getStreetAddress();

      merchantData.EIN = this.data.EIN;
      merchantData.DBA = this.data.DBA;
      merchantData.MID = this.data.MID;
      merchantData.MCC = this.data.MCC;
      merchantData.MCCDescription = this.data.MCCDescription;
      merchantData.legalName = this.data.legalName;
      merchantData.timezone = this.data.timezone;
      merchantData.website = this.data.website;
      merchantData.phone = this.data.phone;
      merchantData.fax = this.data.fax;
      merchantData.brandName = this.data.brandName;
      merchantData.businessType = this.data.businessType;
      merchantData.businessDescription = this.data.businessDescription;
      merchantData.commodityCode = this.data.commodityCode;
      merchantData.integrationEnabled = this.data.integrationEnabled;
      if (address) {
        merchantData.streetAddress = address;
      }
    }
  }

  public validate(): ValidationMessage[] {
    let errors: ValidationMessage[] = [];

    let validatedControls: iValidatedControl[] = [];
    if (this.validatedTextBoxes && this.validatedDropDowns) {
      validatedControls = validatedControls.concat(
        this.validatedTextBoxes.toArray(),
      );
      validatedControls = validatedControls.concat(
        this.validatedDropDowns.toArray(),
      );
      errors = this.formValidator.validate(validatedControls);
    }

    if (this.addressForm) {
      errors = errors.concat(this.addressForm.validate());
    }

    return errors;
  }

  private copyModelData(source: Merchant, destination: Merchant) {
    destination.id = source.id;
    destination.EIN = source.EIN;
    destination.DBA = source.DBA;
    destination.MID = source.MID;
    destination.MCC = source.MCC;
    destination.MCCDescription = source.MCCDescription;
    destination.timezone = source.timezone;
    destination.legalName = source.legalName;
    destination.website = source.website;
    destination.brandName = source.brandName;
    destination.phone = source.phone;
    destination.fax = source.fax;
    destination.businessType = source.businessType;
    destination.businessDescription = source.businessDescription;
    destination.createdOn = source.createdOn;
    destination.commodityCode = source.commodityCode;
    destination.integrationEnabled = source.integrationEnabled;
    if (source.streetAddress) {
      destination.streetAddress = StreetAddress.clone(source.streetAddress);
    }
  }

  public btnEdit_click() {
    if (this.merchant && this.data) {
      this.copyModelData(this.merchant, this.data);
      this.mode = FormMode.Edit;
    }
  }

  public async btnSaveClick() {
    let errors = this.validate();

    // performative, as there are no required fields and formats are enforced by
    // the individual fields
    if (errors.length > 0) {
      return;
    }

    if (this.data) {
      var that = this;

      this.merchantService
        .patchMerchantBusinessInfo(this.data)
        .then(() => {
          if (that.data && that.merchant) {
            //that.copyModelData(savedMerchant.data, that.data);
            that.copyModelData(that.data, that.merchant);

            that.saved.emit(true);

            that.mode = FormMode.View;
          }
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  public btnCancelClick() {
    if (this.merchant && this.data) {
      this.copyModelData(this.merchant, this.data);
      this.mode = FormMode.View;
      this.saved.emit(false);
    }
    UIHelpers.scrollToTopIfOffPage('divMerchantAccountInfo');
  }

  getFloatLabelValue(): FloatLabelType {
    return 'auto';
  }
}
